import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, Menu, Moon, Sun, X } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { OnzoeLogo } from '../brand/OnzoeLogo';

const navItems = [
  { label: 'Home', to: '/' },
  { label: 'Visualizers', to: '/visualizers' },
  { label: 'Pricing', to: '/pricing' },
  { label: 'Early Access', to: '/beta' },
  { label: 'Docs', to: '/docs' },
]

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  pathname: string;
  theme: string;
  onToggleTheme: () => void;
}

const isActivePath = (pathname: string, to: string) => (to === '/' ? pathname === '/' : pathname.startsWith(to));

const MobileMenu: React.FC<MobileMenuProps> = ({ isOpen, onClose, pathname, theme, onToggleTheme }) => {
  useEffect(() => {
    if (!isOpen) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-[#F5F7FA] text-[#05070D] dark:bg-[#05070D] dark:text-white md:hidden">
      <div className="absolute inset-0">
        <img
          src="/images/onzoe-hero.png"
          alt=""
          className="h-full w-full object-cover opacity-36"
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-[#F5F7FA]/84 dark:bg-[#05070D]/82" />
        <div className="absolute inset-0 bg-[linear-gradient(160deg,#F5F7FA_0%,rgba(245,247,250,0.92)_48%,rgba(32,217,255,0.16)_100%)] dark:bg-[linear-gradient(160deg,#05070D_0%,rgba(5,7,13,0.9)_48%,rgba(32,217,255,0.14)_100%)]" />
      </div>

      <div className="relative flex min-h-full flex-col px-4 py-4">
        <div className="flex items-center justify-between border-b border-[#05070D]/12 dark:border-white/12 pb-4">
          <Link
            to="/"
            onClick={onClose}
            className="rounded-sm text-[#05070D] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#147A95] dark:text-white dark:focus-visible:ring-[#20D9FF]"
            aria-label="Onzoe home"
          >
            <OnzoeLogo className="h-auto w-36" />
          </Link>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onToggleTheme}
              className="inline-flex h-11 w-11 items-center justify-center border border-[#05070D]/12 dark:border-white/12 bg-[#05070D]/6 dark:bg-white/8 text-[#05070D] dark:text-white transition-colors hover:bg-[#05070D]/14 dark:hover:bg-white/14 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#147A95] dark:focus-visible:ring-[#20D9FF]"
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {theme === 'dark' ? <Sun className="h-5 w-5" aria-hidden="true" /> : <Moon className="h-5 w-5" aria-hidden="true" />}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-11 w-11 items-center justify-center border border-[#05070D]/12 dark:border-white/12 bg-[#05070D]/6 dark:bg-white/8 text-[#05070D] dark:text-white transition-colors hover:bg-[#05070D]/14 dark:hover:bg-white/14 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#147A95] dark:focus-visible:ring-[#20D9FF]"
              aria-label="Close menu"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>

        <nav className="mt-8" aria-label="Primary navigation">
          {navItems.map((item, index) => {
            const active = isActivePath(pathname, item.to);

            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={onClose}
                aria-current={active ? 'page' : undefined}
                className={`group flex items-center justify-between border-t border-[#05070D]/10 dark:border-white/10 py-5 text-left transition-colors last:border-b ${
                  active ? 'text-[#9A5700] dark:text-[#FFB454]' : 'text-[#05070D] dark:text-white hover:text-[#147A95] dark:hover:text-[#20D9FF]'
                } focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#147A95] dark:focus-visible:ring-[#20D9FF]`}
              >
                <span className="flex items-center gap-4">
                    <span className="w-7 text-xs font-bold uppercase tracking-[0.18em] text-[#4C5965] dark:text-white/62">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span className="text-3xl font-semibold">{item.label}</span>
                </span>
                <ArrowRight aria-hidden="true" className={`h-5 w-5 transition-transform group-hover:translate-x-1 ${active ? 'text-[#9A5700] dark:text-[#FFB454]' : 'text-[#4C5965] dark:text-white/62'}`} />
              </Link>
            );
          })}
        </nav>

        <div className="mt-8 grid gap-3">
          <Link
            to="/beta"
            onClick={onClose}
            className="inline-flex min-h-12 items-center justify-center rounded-md bg-[#FFB454] px-5 py-3 text-sm font-black text-[#05070D] transition-colors hover:bg-[#ffc579] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#147A95] dark:focus-visible:ring-[#20D9FF]"
          >
            Request beta access
            <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
          </Link>
          <Link
            to="/contact"
            onClick={onClose}
            className="inline-flex min-h-12 items-center justify-center rounded-md border border-[#05070D]/14 dark:border-white/14 bg-[#05070D]/6 dark:bg-white/8 px-5 py-3 text-sm font-bold text-[#05070D] dark:text-white transition-colors hover:bg-[#05070D]/14 dark:hover:bg-white/14 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#147A95] dark:focus-visible:ring-[#20D9FF]"
          >
            Talk to sales
          </Link>
        </div>

        <div className="mt-auto pt-10">
          <div className="grid grid-cols-3 gap-2 border-t border-[#05070D]/10 dark:border-white/10 pt-4">
            {['Voice', 'Tours', 'Proof'].map((item) => (
              <div key={item} className="border-l border-[#20D9FF]/28 px-3 py-2">
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#4C5965] dark:text-white/52">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const Header: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  const { theme, setTheme } = useTheme();
  const { pathname } = useLocation();

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
  };

  return (
    <>
      <header className="sticky top-0 z-40 border-b border-[#05070D]/10 dark:border-white/10 bg-[#F5F7FA]/96 dark:bg-[#05070D]/94 px-4 py-3 text-[#05070D] dark:text-white shadow-[0_1px_0_rgba(255,255,255,0.04)] backdrop-blur-xl md:px-6">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4">
          <Link to="/" className="rounded-sm text-[#05070D] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#147A95] dark:text-white dark:focus-visible:ring-[#20D9FF]" aria-label="Onzoe home">
            <OnzoeLogo className="h-auto w-36" />
          </Link>

          <nav className="hidden items-center gap-1 md:flex" aria-label="Primary navigation">
            {navItems.map((item) => {
              const active = isActivePath(pathname, item.to);

              return (
                <Link
                  key={item.to}
                  to={item.to}
                  aria-current={active ? 'page' : undefined}
                  className={`rounded-md px-3 py-2 text-sm font-semibold transition-colors ${
                    active ? 'bg-[#05070D]/8 dark:bg-white/10 text-[#9A5700] dark:text-[#FFB454]' : 'text-[#4C5965] dark:text-white/66 hover:bg-[#05070D]/8 dark:hover:bg-white/8 hover:text-[#05070D] dark:hover:text-white'
                  } focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#147A95] dark:focus-visible:ring-[#20D9FF]`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="hidden items-center gap-2 md:flex">
            <button
              type="button"
              onClick={toggleTheme}
              className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-[#05070D]/10 dark:border-white/10 text-[#4C5965] dark:text-white/72 transition-colors hover:bg-[#05070D]/8 dark:hover:bg-white/8 hover:text-[#05070D] dark:hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#147A95] dark:focus-visible:ring-[#20D9FF]"
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {theme === 'dark' ? <Sun className="h-4 w-4" aria-hidden="true" /> : <Moon className="h-4 w-4" aria-hidden="true" />}
            </button>
            <Link
              to="/contact"
              className="inline-flex h-10 items-center justify-center rounded-md bg-[#FFB454] px-4 text-sm font-black text-[#05070D] transition-colors hover:bg-[#ffc579] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#147A95] dark:focus-visible:ring-[#20D9FF]"
            >
              Contact
            </Link>
          </div>

          <div className="flex items-center gap-2 md:hidden">
            <button
              type="button"
              onClick={toggleTheme}
              className="inline-flex h-10 w-10 items-center justify-center border border-[#05070D]/12 dark:border-white/12 text-[#4C5965] dark:text-white/76 transition-colors hover:bg-[#05070D]/8 dark:hover:bg-white/8 hover:text-[#05070D] dark:hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#147A95] dark:focus-visible:ring-[#20D9FF]"
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {theme === 'dark' ? <Sun className="h-5 w-5" aria-hidden="true" /> : <Moon className="h-5 w-5" aria-hidden="true" />}
            </button>
            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              className="inline-flex h-10 w-10 items-center justify-center border border-[#05070D]/12 dark:border-white/12 bg-[#05070D]/6 dark:bg-white/8 text-[#05070D] dark:text-white transition-colors hover:bg-[#05070D]/14 dark:hover:bg-white/14 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#147A95] dark:focus-visible:ring-[#20D9FF]"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>

      <MobileMenu
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        pathname={pathname}
        theme={theme}
        onToggleTheme={toggleTheme}
      />
    </>
  );
};

export default Header;
